import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface ClaimsTestingFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
}

const MIN_CLAIMS = 5;
const MAX_CLAIMS = 25;

const ClaimsTestingForm: React.FC<ClaimsTestingFormProps> = ({
  onSubmit,
  selectedSegmentIds,
  personaFilters,
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const { audienceData } = useAudience();

  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [productName, setProductName] = useState(initialFormData.productName || '');
  const [productDescription, setProductDescription] = useState(initialFormData.productDescription || '');
  const [category, setCategory] = useState(initialFormData.category || '');
  const [claimsText, setClaimsText] = useState(initialFormData.claimsText || '');
  const [context, setContext] = useState(initialFormData.context || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedClaims = claimsText
    .split('\n')
    .map((line: string) => line.trim())
    .filter((line: string) => line.length > 0);

  useEffect(() => {
    const formData = { simName, productName, productDescription, category, claimsText, context };
    onFormDataChange(formData);
  }, [simName, productName, productDescription, category, claimsText, context]);

  const handleFormChange = () => {
    if (onEditStep) onEditStep();
  };

  const claimsValid = parsedClaims.length >= MIN_CLAIMS && parsedClaims.length <= MAX_CLAIMS;

  const saveMode = !!onSavePayload;

  const isFormValid =
    (saveMode || selectedSegmentIds.length > 0) &&
    productName.trim() !== '' &&
    productDescription.trim() !== '' &&
    category.trim() !== '' &&
    claimsValid;

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const simulationData = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        persona_filters: personaFilters,
        name: simName || `Claims Testing - ${productName}`,
        task: 'claims_testing',
        data: {
          product_name: productName,
          product_description: productDescription,
          category,
          claims: parsedClaims,
          context: context || '',
        },
      };

      if (onSavePayload) {
        onSavePayload(simulationData);
        return;
      }

      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
    } catch (err) {
      console.error('Error starting claims testing:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">
            {selectedSegmentIds.length} segment{selectedSegmentIds.length !== 1 ? 's' : ''} selected
          </span>
        </div>

        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 p-3 rounded-md">{error}</div>
        )}
      </div>

      {/* Simulation Name */}
      <div className="mb-6">
        <label htmlFor="ct-simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name (Optional)
        </label>
        <input
          id="ct-simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => { setSimName(e.target.value); handleFormChange(); }}
          placeholder="e.g., FlavCity Protein Claims Test"
        />
      </div>

      {/* Product Name */}
      <div className="mb-6">
        <label htmlFor="ct-productName" className="block text-sm font-medium text-gray-700 mb-2">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          id="ct-productName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={productName}
          onChange={(e) => { setProductName(e.target.value); handleFormChange(); }}
          placeholder="e.g., FlavCity All-in-One Protein"
        />
      </div>

      {/* Product Description */}
      <div className="mb-6">
        <label htmlFor="ct-productDesc" className="block text-sm font-medium text-gray-700 mb-2">
          Product Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="ct-productDesc"
          rows={3}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={productDescription}
          onChange={(e) => { setProductDescription(e.target.value); handleFormChange(); }}
          placeholder="e.g., A clean-label protein powder with 25g protein, no artificial sweeteners, designed for smoothies and baking."
        />
      </div>

      {/* Category */}
      <div className="mb-6">
        <label htmlFor="ct-category" className="block text-sm font-medium text-gray-700 mb-2">
          Category <span className="text-red-500">*</span>
        </label>
        <input
          id="ct-category"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={category}
          onChange={(e) => { setCategory(e.target.value); handleFormChange(); }}
          placeholder="e.g., Protein Powder"
        />
      </div>

      {/* Claims List */}
      <div className="mb-6">
        <label htmlFor="ct-claims" className="block text-sm font-medium text-gray-700 mb-2">
          Claims List <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2">
            (one claim per line, {MIN_CLAIMS}–{MAX_CLAIMS} claims)
          </span>
        </label>
        <textarea
          id="ct-claims"
          rows={8}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          value={claimsText}
          onChange={(e) => { setClaimsText(e.target.value); handleFormChange(); }}
          placeholder={`25g of protein per serving\nNo artificial sweeteners\nMade with grass-fed whey\nPerfect for smoothies and baking\nClean label — only 6 ingredients`}
        />
        <div className="flex items-center justify-between mt-2">
          <span
            className={`text-xs ${
              parsedClaims.length < MIN_CLAIMS
                ? 'text-amber-600'
                : parsedClaims.length > MAX_CLAIMS
                ? 'text-red-600'
                : 'text-green-600'
            }`}
          >
            {parsedClaims.length} claim{parsedClaims.length !== 1 ? 's' : ''} detected
            {parsedClaims.length < MIN_CLAIMS && ` (minimum ${MIN_CLAIMS})`}
            {parsedClaims.length > MAX_CLAIMS && ` (maximum ${MAX_CLAIMS})`}
          </span>
        </div>

        {parsedClaims.length > 0 && parsedClaims.length <= MAX_CLAIMS && (
          <div className="mt-3 bg-gray-50 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Preview:</p>
            <ul className="space-y-1">
              {parsedClaims.map((claim: string, i: number) => (
                <li key={i} className="text-sm text-gray-700 flex items-start">
                  <span className="text-gray-400 mr-2 font-mono text-xs mt-0.5">{i + 1}.</span>
                  {claim}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Context */}
      <div className="mb-6">
        <label htmlFor="ct-context" className="block text-sm font-medium text-gray-700 mb-2">
          Additional Context
          <span className="text-gray-400 font-normal ml-2">(optional)</span>
        </label>
        <textarea
          id="ct-context"
          rows={3}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={context}
          onChange={(e) => { setContext(e.target.value); handleFormChange(); }}
          placeholder="Any other info that you think is relevant — competitive landscape, target demographics, marketing channel, etc."
        />
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting ? 'Running Claims Test...' : saveMode ? 'Continue' : 'Run Claims Test'}
        </Button>
      </div>
    </div>
  );
};

export default ClaimsTestingForm;
